import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form, Input, Spin } from 'antd';
import { api, errorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { AuthCard } from '../../components/AuthCard';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, user, logout, acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<{ email: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing invite token');
      return;
    }
    api
      .get<{ email: string; name: string }>(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(errorMessage(e, 'Invite is invalid or expired')));
  }, [token]);

  const onFinish = async (values: { name: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      await acceptInvite(token, values.name, values.password);
      navigate('/', { replace: true });
    } catch (e) {
      setError(errorMessage(e, 'Could not accept invite'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title="Accept your invite" subtitle={invite ? `Signing up as ${invite.email}` : undefined}>
      {error && <Alert type="error" title={error} style={{ marginBottom: 16 }} showIcon />}
      {!invite && !error && <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>}
      {invite && status === 'authenticated' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`You are signed in as ${user?.email ?? 'another user'}`}
          description="Sign out first to accept this invite with a new account."
          action={<Button size="small" onClick={() => void logout()}>Sign out</Button>}
        />
      )}
      {invite && status !== 'authenticated' && (
        <Form layout="vertical" onFinish={onFinish} requiredMark={false} initialValues={{ name: invite.name }}>
          <Form.Item name="name" label="Your name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="Choose a password"
            rules={[{ required: true, min: 10, message: 'At least 10 characters' }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>
            Create account
          </Button>
        </Form>
      )}
      {error && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Link to="/login">Back to sign in</Link>
        </div>
      )}
    </AuthCard>
  );
}
